import { useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $cardLimitsStore, $cardStore } from '@lib/store';
import { type UpdateLimitCardRequest, Frequency } from '@shared/api/rest/cards';
import { CardNewLimitRow } from '@widgets';
import { Button, Modal, Select, Text } from '@shared/widgets';
import { useCurrencySymbol } from '@shared/hooks';

const FREQUENCIES = [
  { value: Frequency.DAILY, label: 'sidebar.modals.requestNewLimits.frequencies.daily' },
  { value: Frequency.CYCLE, label: 'sidebar.modals.requestNewLimits.frequencies.cycle' },
  { value: Frequency.YEAR, label: 'sidebar.modals.requestNewLimits.frequencies.year' },
];

interface RequestNewLimitsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const RequestNewLimitsModal = (props: RequestNewLimitsModalProps) => {
  const cardStore = useUnit($cardStore.store);
  const cardLimitsStore = useUnit($cardLimitsStore.store);
  const limits = cardLimitsStore.data!;

  const [frequency, setFrequency] = useState<Frequency>(limits.frequency);
  const [newLimits, setNewLimits] = useState<UpdateLimitCardRequest>({
    retailNumber: limits.currentRetailNumber,
    retailAmount: limits.currentRetailAmount,
    cashAmount: limits.currentCashAmount,
    cashNumber: limits.currentCashNumber,
    otcAmount: limits.currentOTCAmount,
    otcNumber: limits.currentOTCNumber,
    frequency: limits.frequency,
  });

  const { t } = useTranslation('card');

  const closeModal = () => {
    setNewLimits({
      retailNumber: limits.currentRetailNumber,
      retailAmount: limits.currentRetailAmount,
      cashAmount: limits.currentCashAmount,
      cashNumber: limits.currentCashNumber,
      otcAmount: limits.currentOTCAmount,
      otcNumber: limits.currentOTCNumber,
      frequency: frequency,
    });
    props.onClose();
  };
  const onChange = (part: Partial<UpdateLimitCardRequest>) => setNewLimits({ ...newLimits, ...part });
  const saveHandler = async () => {
    await $cardLimitsStore.events.updateLimitCard({ cardUUId: cardStore.data!.cardGuid, ...newLimits, frequency: frequency });
    props.onClose();
  };

  const symbol = useCurrencySymbol(cardStore.data?.currency);
  const currentLimitValues = useMemo(() => {
    if (limits.frequency === Frequency.DAILY) return limits.dayLimits;
    else if (limits.frequency === Frequency.CYCLE) return limits.cycleLimits;
    else if (limits.frequency === Frequency.YEAR) return limits.yearLimits;
    else return limits.dayLimits;
  }, [limits, frequency]);
  return (
    <Modal
      isOpen={props.isOpen}
      onClose={closeModal}
      loading={cardLimitsStore.actionLoading}
      title={t('sidebar.modals.requestNewLimits.title')}
      width='950px'
    >
      <div className='mb-5 w-[10em]'>
        <Text>{t('sidebar.modals.requestNewLimits.placeholders.limitFrequency')}</Text>
        <Select
          items={FREQUENCIES.map((option) => ({ ...option, label: t(option.label) }))}
          value={frequency}
          onChange={(val) => setFrequency(val as Frequency)}
        />
      </div>
      <div className='-mx-5 flex justify-between'>
        <CardNewLimitRow
          limitRowLabel={t('sidebar.modals.requestNewLimits.sections.retailLimit')}
          currentAmount={currentLimitValues?.retailAmount || 0}
          limitAmount={limits.currentRetailAmount}
          limitNumber={limits.currentRetailNumber}
          currentNumber={currentLimitValues?.retailNumber || 0}
          symbol={symbol}
          firstInputLabel={t('sidebar.modals.requestNewLimits.placeholders.newAmount')}
          firstInputValue={newLimits.retailAmount}
          firstInputOnChange={(value) => onChange({ retailAmount: parseFloat(value) })}
          firstInputLimitValue={newLimits.retailAmount}
          secondInputLabel={t('sidebar.modals.requestNewLimits.placeholders.newTxnNumber')}
          secondInputValue={newLimits.retailNumber}
          secondInputOnChange={(value) => onChange({ retailNumber: parseInt(value) })}
          secondInputLimitValue={newLimits.retailNumber}
        />
        <CardNewLimitRow
          limitRowLabel={t('sidebar.modals.requestNewLimits.sections.cashLimit')}
          limitAmount={limits.currentCashAmount}
          currentAmount={currentLimitValues?.cashAmount || 0}
          limitNumber={limits.currentCashNumber}
          currentNumber={currentLimitValues?.cashNumber || 0}
          symbol={symbol}
          firstInputLabel={t('sidebar.modals.requestNewLimits.placeholders.newAmount')}
          firstInputValue={newLimits.cashAmount}
          firstInputOnChange={(value) => onChange({ cashAmount: parseFloat(value) })}
          firstInputLimitValue={newLimits.cashAmount}
          secondInputLabel={t('sidebar.modals.requestNewLimits.placeholders.newTxnNumber')}
          secondInputValue={newLimits.cashNumber}
          secondInputOnChange={(value) => onChange({ cashNumber: parseInt(value) })}
          secondInputLimitValue={newLimits.cashNumber}
        />
        <CardNewLimitRow
          limitRowLabel={t('sidebar.modals.requestNewLimits.sections.totalLimit')}
          limitAmount={limits.currentOTCAmount}
          currentAmount={currentLimitValues?.otcAmount || 0}
          limitNumber={limits.currentOTCNumber}
          currentNumber={currentLimitValues?.otcNumber || 0}
          symbol={symbol}
          firstInputLabel={t('sidebar.modals.requestNewLimits.placeholders.newAmount')}
          firstInputValue={newLimits.otcAmount}
          firstInputOnChange={(value) => onChange({ otcAmount: parseFloat(value) })}
          firstInputLimitValue={newLimits.otcAmount}
          secondInputLabel={t('sidebar.modals.requestNewLimits.placeholders.newTxnNumber')}
          secondInputValue={newLimits.otcNumber}
          secondInputOnChange={(value) => onChange({ otcNumber: parseInt(value) })}
          secondInputLimitValue={newLimits.otcNumber}
        />
      </div>
      <div className='text-center'>
        <Button
          color='primary'
          disabled={Number.isNaN(newLimits.retailAmount) || Number.isNaN(newLimits.cashAmount) || Number.isNaN(newLimits.otcAmount)}
          onClick={saveHandler}
        >
          {t('sidebar.modals.requestNewLimits.buttons.requestNewLimits')}
        </Button>
      </div>
    </Modal>
  );
};
